import { useMemo } from 'react';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import { tagAssociationQueryKeys } from '~/hooks/apis/tags/useGetTagAssociations';

import TagsService, { type CreateTagInput, type GetTagsParams, type UpdateTagInput } from '~/services/TagsService';
import { isBetween } from '~/services/TimeService';

type TagAssignmentCounts = {
  destinations: number;
  hotels: number;
  experiences: number;
};

type TagScheduleSummary = {
  id: string;
  text: string;
  isActive: boolean;
};

export function useGetTags(params: GetTagsParams) {
  return useQuery({
    queryKey: ['tags', params.offset, params.limit, params.sortBy, params.sortOrder, params.name, params.tagType],
    queryFn: () => TagsService.getTags(params),
  });
}

export function useGetTag(tagId: string) {
  return useQuery({
    queryKey: ['tags', tagId],
    queryFn: () => TagsService.getTag(tagId),
    enabled: !!tagId,
  });
}

export function useGetTagsMetadata(tagIds: Array<string>) {
  const tagQueries = useQueries({
    queries: tagIds.map((tagId) => ({
      queryKey: ['tags', tagId],
      queryFn: () => TagsService.getTag(tagId),
      enabled: !!tagId,
    })),
  });

  const assignmentQueries = useQueries({
    queries: tagIds.map((tagId) => ({
      queryKey: ['tag-assignments', tagId, 0, 2000],
      queryFn: () => TagsService.getTagAssignments({ tagId, offset: 0, limit: 2000 }),
      enabled: !!tagId,
    })),
  });

  const regionMap = useMemo(() => {
    return new Map(getRegionNamesAndCode().map((region) => [region.code, region.name]));
  }, []);

  const assignmentCounts = useMemo(() => {
    const counts = new Map<string, TagAssignmentCounts>();

    tagIds.forEach((tagId, index) => {
      const assignments = assignmentQueries[index]?.data?.result ?? [];

      counts.set(
        tagId,
        assignments.reduce<TagAssignmentCounts>(
          (acc, assignment) => {
            if (assignment.entityType === 'DESTINATION') {
              acc.destinations += 1;
            }

            if (assignment.entityType === 'HOTEL') {
              acc.hotels += 1;
            }

            if (assignment.entityType === 'EXPERIENCE') {
              acc.experiences += 1;
            }

            return acc;
          },
          {
            destinations: 0,
            hotels: 0,
            experiences: 0,
          },
        ),
      );
    });

    return counts;
  }, [tagIds, assignmentQueries]);

  const scheduleSummaries = useMemo(() => {
    const summaries = new Map<string, Array<TagScheduleSummary>>();

    tagIds.forEach((tagId, index) => {
      const schedules = tagQueries[index]?.data?.schedules ?? [];

      summaries.set(
        tagId,
        schedules.map((schedule) => {
          const brandText = schedule.brands.length > 0 ? schedule.brands.join(', ') : 'N/A';
          const regionText =
            schedule.regions.length === 0
              ? 'N/A'
              : schedule.regions.length === regionMap.size
                ? 'All regions'
                : schedule.regions.map((regionCode) => regionMap.get(regionCode) ?? regionCode).join(', ');
          const isActive = isBetween(schedule.activeStartDate, schedule.activeEndDate, undefined, undefined, '[]');
          const activeText = isActive ? 'Active' : 'Inactive';

          return {
            id: schedule.id,
            text: `${brandText}, ${regionText}, ${activeText}`,
            isActive,
          };
        }),
      );
    });

    return summaries;
  }, [regionMap, tagIds, tagQueries]);

  const inactiveTagIds = useMemo(() => {
    const ids = new Set<string>();

    tagIds.forEach((tagId) => {
      const schedules = scheduleSummaries.get(tagId);
      const hasActiveSchedule = schedules?.some((schedule) => schedule.isActive);

      if (!hasActiveSchedule) {
        ids.add(tagId);
      }
    });

    return ids;
  }, [tagIds, scheduleSummaries]);

  return {
    assignmentCounts,
    scheduleSummaries,
    inactiveTagIds,
    isLoading: tagQueries.some((query) => query.isLoading) || assignmentQueries.some((query) => query.isLoading),
    isError: tagQueries.some((query) => query.isError) || assignmentQueries.some((query) => query.isError),
  };
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: CreateTagInput) => TagsService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      enqueueSnackbar('Tag created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating tag: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagInput }) => TagsService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: tagAssociationQueryKeys.all });
      enqueueSnackbar('Tag updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating tag: ${error.message}`, { variant: 'error' });
    },
  });
}
